import React, { useEffect, useRef, useState } from "react";
import AppLayout from "../components/AppLayout";
import TalkingHead from "../components/TalkingHead";
import VoiceBar from "../components/VoiceBar";
import { useVoice } from "../context/VoiceContext";
import { api } from "../lib/api";
import { Send, Flame, Volume2 } from "lucide-react";
import { toast } from "sonner";

const INTENSITY = ["casual", "flirty", "explicit"];

export default function Sexting() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [intensity, setIntensity] = useState("flirty");
  const [toyUpsell, setToyUpsell] = useState(false);
  const [busy, setBusy] = useState(false);
  const { speak, autoSpeak } = useVoice();
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const text = input;
    setInput(""); setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }]);
    try {
      const r = await api.post("/api/chat/send", {
        session_id: sessionId, content: text, mode: "sexting",
        intensity, toy_upsell: toyUpsell,
      });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, r.data.assistant_message]);
      if (autoSpeak && r.data.assistant_message?.content) {
        speak(r.data.assistant_message.content);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const newSession = () => { setSessionId(null); setMessages([]); };

  const intensityIdx = INTENSITY.indexOf(intensity);

  return (
    <AppLayout title="Sexting mode" overline="100 · DRAFT HOT REPLIES · ADULT-ONLY">
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap fade-up">
        <div className="flex items-center gap-4">
          <TalkingHead size={90} />
          <div>
            <div className="overline text-[color:var(--gio-primary)]">SEXTING · {intensity.toUpperCase()}</div>
            <div className="font-heading text-2xl text-white italic">Ready for you.</div>
          </div>
        </div>
        <VoiceBar />
      </div>
      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        <aside className="border border-zinc-900 bg-zinc-950 p-6 h-fit fade-up" data-testid="sexting-controls">
          <div className="overline mb-3">Intensity</div>
          <input type="range" min={0} max={2} step={1} value={intensityIdx}
            onChange={(e) => setIntensity(INTENSITY[parseInt(e.target.value)])}
            data-testid="intensity-slider"
            className="gio-slider w-full" />
          <div className="flex justify-between text-[10px] font-mono-ui uppercase tracking-wider text-zinc-600 mt-2">
            {INTENSITY.map((v) => (
              <span key={v} className={intensity === v ? "text-[color:var(--gio-primary)]" : ""} data-testid={`intensity-${v}`}>{v}</span>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-between">
            <div className="overline">Toy upsell</div>
            <button onClick={() => setToyUpsell(!toyUpsell)} data-testid="toy-upsell-toggle"
              className={`w-12 h-6 border transition relative ${toyUpsell ? "border-[color:var(--gio-primary)] bg-[color:var(--gio-primary)]/20" : "border-zinc-800"}`}>
              <span className={`absolute top-0.5 w-4 h-4 transition ${toyUpsell ? "left-7 bg-[color:var(--gio-primary)]" : "left-0.5 bg-zinc-600"}`} />
            </button>
          </div>

          <button onClick={newSession} data-testid="sexting-new-session" className="btn-ghost mt-8 w-full">
            Reset session
          </button>

          <div className="overline mt-8 text-zinc-700 text-[10px] leading-relaxed">
            DRAFTING AID ONLY. NO MINORS. NO NON-CONSENT. YOU ARE RESPONSIBLE FOR WHAT YOU SEND.
          </div>
        </aside>

        <section className="border border-zinc-900 bg-black flex flex-col min-h-[70vh]">
          <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="sexting-messages">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <Flame size={48} className="text-[color:var(--gio-primary)] mb-6" />
                <div className="overline mb-3">SEXTING MODE · {intensity.toUpperCase()}</div>
                <div className="font-heading text-3xl text-white mb-2">Pick up the thread.</div>
                <div className="text-zinc-500 text-sm max-w-sm">Paste the fan's last message to draft your hot reply. The Co-Pilot mirrors their language and stays in your voice.</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`sexting-msg-${m.role}-${i}`}>
                <div className={`max-w-[80%] p-4 text-sm whitespace-pre-wrap group relative ${
                  m.role === "user"
                    ? "bg-zinc-900 border border-zinc-800 text-white rounded-tr-none"
                    : "bg-transparent border border-[color:var(--gio-primary)] text-[color:var(--gio-primary)] rounded-tl-none shadow-[0_0_18px_rgba(255,51,0,0.22)]"
                }`}>{m.content}
                  {m.role === "assistant" && (
                    <button onClick={() => speak(m.content)} data-testid={`sexting-speak-${i}`}
                      className="absolute -top-2 -right-2 p-1 bg-black border border-[color:var(--gio-primary)] opacity-0 group-hover:opacity-100 transition text-[color:var(--gio-primary)]">
                      <Volume2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {busy && <div className="overline text-[color:var(--gio-primary)]">COOKING...</div>}
            <div ref={endRef} />
          </div>

          <div className="border-t border-zinc-900 p-4 flex gap-3">
            <textarea value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Paste fan's message or set the scene..."
              data-testid="sexting-input"
              rows={2}
              className="flex-1 bg-zinc-950 border border-zinc-900 text-white p-3 text-sm resize-none focus:outline-none focus:border-[color:var(--gio-primary)]" />
            <button onClick={send} disabled={busy || !input.trim()} data-testid="sexting-send-button"
              className="btn-primary flex items-center gap-2 disabled:opacity-40">
              <Send size={16} /> Send
            </button>
          </div>
        </section>
      </div>
    </AppLayout>
  );
}
